import React from "react";
import SectionTitle from "@others/title-section.layout";
import {
    Box,
    Container,
    Divider,
    Typography,
    Stack,
    Paper,
    Button,
    Alert, Chip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBan, faBuilding,
    faDownload, faExclamationTriangle,
    faExternalLinkAlt,
    faFilePdf, faHome,
    faInfoCircle,
    faLightbulb, faQuestionCircle, faTools
} from "@fortawesome/free-solid-svg-icons";
// @ts-ignore
import oldLogo from "@logo/logo-old.png";

const ARTICLE20250621: React.FC = () => {
    return (
        <Container maxWidth="lg" sx={{ mb: 6 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />
            <Stack spacing={3}>
                <Box textAlign="center">
                    <img
                        src={oldLogo}
                        alt="Association Vivre la démocratie à BLR"
                        style={{ width: 300, marginBottom: 10, marginTop: 10 }}
                    />
                    <Typography
                        variant="h5"
                        color="primary"
                        fontWeight={700}
                        gutterBottom
                        sx={{ pt: 1, pb: 1 }}
                    >
                        Extrait du PV de synthèse de la commission d'enquête publique du PLUi (page 5)
                    </Typography>
                </Box>
                <Divider />

                <Typography variant="subtitle1" color="primary" fontWeight={700} gutterBottom>
                    La mixité sociale à Bois-le-Roi
                </Typography>

                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    De nombreuses observations ont été émises pour exprimer une vive opposition aux secteurs de mixité sociale prévus dans la commune de Bois-le-Roi.
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <FontAwesomeIcon icon={faExclamationTriangle} color="#ef9a3a" />
                    <Typography fontWeight={700}>Points contestés</Typography>
                </Stack>
                <Stack component="ul" sx={{ pl: 2, mb: 2 }} spacing={1}>
                    <Stack component="li" direction="row" spacing={1}>
                        <FontAwesomeIcon icon={faBan} color="#d32f2f" />
                        <Typography component="span">
                            La mixité sociale en général, vue comme un échec ("la mixité sociale, ça ne marche pas") et source d’inquiétude concernant les nouveaux habitants (certains quartiers ou villes du 77, notamment Melun, sont cités).
                        </Typography>
                    </Stack>
                    <Stack component="li" direction="row" spacing={1}>
                        <FontAwesomeIcon icon={faHome} color="#0277bd" />
                        <Typography component="span">
                            Les besoins accrus en équipements et services, attribués notamment aux futurs locataires du parc social, qui pourraient alourdir les finances communales (et impacter les impôts locaux).
                        </Typography>
                    </Stack>
                    <Stack component="li" direction="row" spacing={1}>
                        <FontAwesomeIcon icon={faTools} color="#ffca28" />
                        <Box>
                            <Typography component="span">
                                Les outils du PLUi (Article 3.4), et en particulier les SMS (secteurs de mixité sociale) qui imposent 80% de logements sociaux dans les nouveaux programmes. Les SMS sont critiqués&nbsp;:
                            </Typography>
                            <Stack component="ul" spacing={0.5} sx={{ pl: 3, mt: 1, mb: 0 }}>
                                <li>
                                    <Typography component="span" variant="body2">Non conformité au code de l’urbanisme (s’applique dès le premier logement construit)</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Taux trop élevé (80&nbsp;%), jugé non créateur de mixité et potentiellement contre-productif</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Manque de concertation/information des propriétaires concernés, annonce tardive</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Critères non précisés</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">SMS concentrés dans certaines rues (notamment rue Moreau de Tours)</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Parcelles parfois inadaptées (trop petites, peu constructibles)</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">SMS nécessitant l’accord simultané de plusieurs propriétaires sur des jardins mitoyens</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Atteinte au droit de disposer librement de son bien (risques de préemption/expropriation)</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Baisse de la valeur des biens</Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">Mise en œuvre jugée imprécise et opaque</Typography>
                                </li>
                            </Stack>
                        </Box>
                    </Stack>
                    <Stack component="li" direction="row" spacing={1}>
                        <FontAwesomeIcon icon={faLightbulb} color="#ffee58" />
                        <Box>
                            <Typography component="span">
                                Contre-propositions exprimées par des habitants pourtant favorables à la mixité sociale&nbsp;:
                            </Typography>
                            <Stack component="ul" spacing={0.5} sx={{ pl: 3, mt: 1, mb: 0 }}>
                                <li>
                                    <Typography component="span" variant="body2">
                                        Mobilisation des logements vacants (plusieurs dizaines estimés)
                                    </Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">
                                        Aide à la rénovation des logements privés pour location à loyer modéré
                                    </Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">
                                        Réutilisation de bâtiments communaux vacants (ex&nbsp;: bibliothèque fermée)
                                    </Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">
                                        Acquisition de biens par la commune, à l’amiable ou par préemption
                                    </Typography>
                                </li>
                                <li>
                                    <Typography component="span" variant="body2">
                                        Augmentation du taux de logement social dans les OAP
                                    </Typography>
                                </li>
                            </Stack>
                        </Box>
                    </Stack>
                    <Stack component="li" direction="row" spacing={1}>
                        <FontAwesomeIcon icon={faBan} color="#d32f2f" />
                        <Typography component="span">
                            Nombreux appels à la suppression de l’Article 3.4, parfois jusqu’à la sortie de la CAPF pour échapper à la loi SRU.
                        </Typography>
                    </Stack>
                    <Stack component="li" direction="row" spacing={1}>
                        <FontAwesomeIcon icon={faBuilding} color="#9381ff" />
                        <Typography component="span">
                            Disposition générale sur la mixité&nbsp;: taux de 80&nbsp;% ou 75&nbsp;% (si programme : 16 logements) de logements sociaux dans toute la commune en dehors des OAP et SMS&nbsp;; proposition d'appliquer cette règle aussi aux divisions de logements existants.
                        </Typography>
                    </Stack>
                </Stack>
                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <FontAwesomeIcon icon={faQuestionCircle} color="#42a5f5" />
                    <Typography fontWeight={700}>Questions de la commission d'enquête</Typography>
                </Stack>
                <Stack component="ol" sx={{ pl: 3 }} spacing={1.5}>
                    <li>
                        <Typography component="span">
                            Mixité sociale et SMS à Bois-le-Roi&nbsp;: comment ont été déterminés les taux de 80&nbsp;% et 75&nbsp;% de logements sociaux&nbsp;?
                        </Typography>
                    </li>
                    <li>
                        <Typography component="span">
                            Pour faciliter les acquisitions foncières des terrains faisant l’objet d’un SMS, est-il envisagé une convention de portage foncier ou une délégation de droit de préemption&nbsp;?
                        </Typography>
                    </li>
                </Stack>


                <Alert
                    severity="info"
                    icon={<FontAwesomeIcon icon={faInfoCircle} />}
                    sx={{ mt: 2 }}
                >
                    Pour plus d’informations et pour lire le compte-rendu officiel complet, consultez le site de la CAPF ou téléchargez le document :
                </Alert>

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        component="a"
                        href="https://www.pays-fontainebleau.fr/le-plan-local-durbanisme-intercommunal/"
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                        sx={{ minWidth: 200 }}
                    >
                        Visiter le site officiel
                    </Button>
                </Stack>

                {/* Bloc téléchargement tract/document */}
                <Box
                    sx={{
                        mt: 5,
                        mb: 5,
                        bgcolor: "#f7f3ee",
                        borderRadius: 3,
                        p: 4,
                        boxShadow: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faFilePdf} size="2x" style={{ color: "#d32f2f" }} />
                        <Typography variant="h6" fontWeight={700} color="primary">
                            Télécharger le compte-rendu au format PDF
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={2} align="center">
                        Voici le compte-rendu de la CAPF sur le PLUi, disponible en PDF pour lecture, impression ou partage.
                    </Typography>
                    <a
                        href="/files/EP-PLUI-CAPF-Proces-verbal-de-synthese-de-la-CE.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            textDecoration: "none",
                        }}
                    >
                        <Chip
                            icon={<FontAwesomeIcon icon={faDownload} style={{ color: "#fff" }} />}
                            label="Télécharger le PDF"
                            sx={{
                                bgcolor: "#d32f2f",
                                color: "#fff",
                                px: 3,
                                py: 2,
                                fontWeight: 600,
                                fontSize: "1rem",
                                "&:hover": {
                                    bgcolor: "#b71c1c",
                                },
                            }}
                            clickable
                        />
                    </a>
                </Box>
                {/* Fin du bloc tract/document */}
            </Stack>
        </Container>
    );
};

export default ARTICLE20250621;